/**
 * Shared visual atoms for the tenant profile (TenantProfileDialog + its tabs).
 * Inline-style helpers that mirror the handoff prototype exactly so both the
 * shell and the tab content render pixel-identically.
 */
import { Fragment } from "react";
import type { CSSProperties, ReactNode } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Persistent Undo affordance for a just-completed lifecycle action. Shared so
 * it can render either at the dialog shell level or inline next to a specific
 * tab's action (e.g. move-in), instead of hardcoding one location.
 */
export function UndoBanner({
  label,
  onUndo,
  onDismiss,
  busy,
  style,
}: {
  label: string;
  onUndo: () => void;
  onDismiss: () => void;
  busy?: boolean;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        padding: "11px 16px",
        borderRadius: 14,
        background: "#FEF6E7",
        border: "1px solid #F7E4BD",
        display: "flex",
        alignItems: "center",
        gap: 12,
        ...style,
      }}
    >
      <svg
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#B45309"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0 }}
      >
        <path d="M9 14 4 9l5-5" />
        <path d="M4 9h11a5 5 0 0 1 0 10h-1" />
      </svg>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 13,
          fontWeight: 600,
          color: "#8A5A0B",
        }}
      >
        {label}
      </div>
      <button
        onClick={onUndo}
        disabled={busy}
        style={{
          border: "1px solid #E7D3A6",
          background: "#fff",
          color: "#B45309",
          borderRadius: 9,
          padding: "7px 14px",
          fontSize: 12.5,
          fontWeight: 700,
          cursor: busy ? "default" : "pointer",
          whiteSpace: "nowrap",
        }}
      >
        Undo
      </button>
      <button
        onClick={onDismiss}
        title="Dismiss"
        style={{
          border: "none",
          background: "transparent",
          color: "#B45309",
          cursor: "pointer",
          display: "flex",
          flexShrink: 0,
        }}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export function chip(bg: string, text: string, border: string): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 11,
    fontWeight: 700,
    padding: "3px 9px",
    borderRadius: 999,
    background: bg,
    color: text,
    border: `1px solid ${border}`,
  };
}

export const CHIP = {
  green: chip("#E6F7EF", "#047857", "#C6EEDB"),
  amber: chip("#FEF6E7", "#B45309", "#F7E4BD"),
  amber2: chip("#FFF1E8", "#C2410C", "#FBD6BF"),
  cyan: chip("#E5FAFD", "#0E7490", "#C4EFF6"),
  /** In-progress, admin-only state — e.g. a contract draft not yet sent. */
  blue: chip("#EAF1FE", "#1D4ED8", "#D3E1FD"),
  violet: chip("#F1ECFE", "#6D28D9", "#E1D5FB"),
  /** Something is wrong on the record itself — e.g. a high-severity defect. */
  rose: chip("#FDECEC", "#B91C1C", "#F8D4D4"),
  slate: chip("#F1F3F6", "#94A3B8", "#E5E7EB"),
  slate2: chip("#F1F3F6", "#64748B", "#E5E7EB"),
};

export const cardWrap: CSSProperties = {
  background: "#F6F7F9",
  border: "1px solid #ECEEF1",
  borderRadius: 24,
  padding: "22px 24px",
};

const whiteCardHead: CSSProperties = {
  background: "#fff",
  border: "1px solid #ECEEF1",
  borderRadius: 20,
  overflow: "hidden",
};

export const whitePanel: CSSProperties = {
  background: "#fff",
  border: "1px solid #EDEFF2",
  borderRadius: 18,
};

/**
 * Small outline button used by the payment-row actions.
 *
 * Lives here rather than in tenant-profile-tabs.tsx so the receipts strip can
 * use it too — importing it from that file would make the module graph
 * circular, since it imports the strip.
 */
export function actionBtn(color: string): CSSProperties {
  return {
    border: "1px solid #E7E9EC",
    background: "#fff",
    color,
    borderRadius: 9,
    padding: "6px 11px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
  };
}

export const uppLabel: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: ".07em",
  color: "#94A3B8",
  textTransform: "uppercase",
};

export const innerBox: CSSProperties = {
  background: "#FAFBFC",
  border: "1px solid #F0F1F3",
  borderRadius: 14,
};

export function Chevron({ open, size = 20 }: { open: boolean; size?: number }) {
  return (
    <div
      style={{
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform .2s",
        color: "#94A3B8",
        flexShrink: 0,
        display: "flex",
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </div>
  );
}

export function SectionIcon({
  bg,
  color,
  size = 38,
  radius = 12,
  children,
}: {
  bg: string;
  color: string;
  size?: number;
  radius?: number;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: bg,
        color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {children}
    </div>
  );
}

/** A white collapsible card with an icon + title + subtitle header row. */
export function CollapsibleCard({
  iconBg,
  iconColor,
  icon,
  title,
  subtitle,
  right,
  action,
  open,
  onToggle,
  children,
}: {
  iconBg: string;
  iconColor: string;
  icon: ReactNode;
  title: string;
  subtitle?: string;
  /** Read-only summary shown in the header (amount, status chip). */
  right?: ReactNode;
  /**
   * A real, clickable control in the header — kept OUTSIDE the toggle button,
   * because a <button> cannot legally contain another one. Cards that pass this
   * get a split header: the summary area toggles, the action does its own thing.
   * Without it the header renders exactly as it always has, so no other card
   * shifts by a pixel.
   */
  action?: ReactNode;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const summary = (
    <>
      <SectionIcon bg={iconBg} color={iconColor} size={40}>
        {icon}
      </SectionIcon>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
        {subtitle && (
          <div
            style={{
              fontSize: 12.5,
              color: "#94A3B8",
              fontWeight: 500,
              marginTop: 2,
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      {right}
    </>
  );

  const toggleStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 13,
    background: "none",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
  };

  return (
    <div style={whiteCardHead}>
      {action ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "18px 20px 18px 24px",
          }}
        >
          <button
            onClick={onToggle}
            aria-expanded={open}
            style={{ ...toggleStyle, flex: 1, minWidth: 0, padding: 0 }}
          >
            {summary}
          </button>
          {action}
          <button
            onClick={onToggle}
            aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
            style={{ ...toggleStyle, padding: 2, gap: 0 }}
          >
            <Chevron open={open} />
          </button>
        </div>
      ) : (
        <button
          onClick={onToggle}
          aria-expanded={open}
          style={{ ...toggleStyle, width: "100%", padding: "20px 24px" }}
        >
          {summary}
          <Chevron open={open} />
        </button>
      )}
      {open && <div style={{ borderTop: "1px solid #F0F1F3" }}>{children}</div>}
    </div>
  );
}

/* ─────────────────────── icons (lucide geometry) ───────────────────────── */

/**
 * The profile's icon primitive, and the reason it is a primitive.
 *
 * Every icon on these screens is drawn from lucide geometry at the same stroke
 * weight and cap style, so a row of controls reads as one set. Sections that
 * reached for text glyphs instead (◉ ↗ ↓ ▤ 🗂 ⓘ) rendered at whatever weight
 * and baseline the platform font happened to have — different on macOS and
 * Windows, and emoji in colour on both — which is what made the deposit card
 * look like a different product from the cards above it.
 */
function icon(path: ReactNode, size = 18): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {path}
    </svg>
  );
}

/** A document. */
export const FileTextIcon = ({ size = 18 }: { size?: number }) =>
  icon(
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M8 13h8M8 17h5" />
    </>,
    size
  );

/** A stack of documents — the bundle, and the section that assembles it. */
export const FolderIcon = ({ size = 18 }: { size?: number }) =>
  icon(
    <>
      <path d="M4 20a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2z" />
      <path d="M2 11h20" />
    </>,
    size
  );

/** Open elsewhere — a new tab, never a download. */
export const ExternalLinkIcon = ({ size = 18 }: { size?: number }) =>
  icon(
    <>
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
    </>,
    size
  );

/** Save a copy. */
export const DownloadIcon = ({ size = 18 }: { size?: number }) =>
  icon(
    <>
      <path d="M12 3v12" />
      <path d="m7 12 5 5 5-5" />
      <path d="M21 21H3" />
    </>,
    size
  );

/** Done. */
export const CheckIcon = ({ size = 18 }: { size?: number }) =>
  icon(<path d="M20 6 9 17l-5-5" />, size);

/** Something is wrong and someone has to act. */
export const AlertIcon = ({ size = 18 }: { size?: number }) =>
  icon(
    <>
      <path d="M12 3 2 20h20z" />
      <path d="M12 9v5M12 17h.01" />
    </>,
    size
  );

/** Explanatory, never a warning. */
export const InfoIcon = ({ size = 18 }: { size?: number }) =>
  icon(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>,
    size
  );

/**
 * One action a payment row can offer. `style` is only read for the action
 * promoted to the row's primary button; inside the menu every item renders
 * identically, because a dropdown that styles its items by severity is how you
 * end up with a red "Delete" that nobody reads before clicking.
 */
export type RowAction = {
  key: string;
  label: string;
  icon?: ReactNode;
  /** Rendered right-aligned and muted, so the labels stay in one column. */
  count?: number;
  onSelect: () => void;
  disabled?: boolean;
  style?: CSSProperties;
  /** Draw a divider above this item — groups corrections apart from actions. */
  separatorBefore?: boolean;
  /** Removes something irreversibly. Always sits last, behind a divider. */
  destructive?: boolean;
};

const rowIconProps = {
  width: 13,
  height: 13,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export const IcEdit = () => (
  <svg {...rowIconProps}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);
export const IcUpload = () => (
  <svg {...rowIconProps}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
  </svg>
);
export const IcProof = () => (
  <svg {...rowIconProps}>
    <path d="m21.44 11.05-9.19 9.19a5 5 0 0 1-7.07-7.07l8.49-8.49a3 3 0 0 1 4.24 4.24l-8.49 8.49a1 1 0 0 1-1.41-1.41l7.78-7.78" />
  </svg>
);
export const IcNotes = () => (
  <svg {...rowIconProps}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
  </svg>
);
export const IcPlus = () => (
  <svg {...rowIconProps}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);
export const IcBell = () => (
  <svg {...rowIconProps}>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);
export const IcBank = () => (
  <svg {...rowIconProps}>
    <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h1m4 0h1M9 13h1m4 0h1M9 17h1m4 0h1" />
  </svg>
);
export const IcEye = () => (
  <svg {...rowIconProps}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
export const IcTrash = () => (
  <svg {...rowIconProps}>
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
  </svg>
);
export const IcReceipt = () => (
  <svg {...rowIconProps}>
    <path d="M4 2v20l2.5-1.5L9 22l3-1.5L15 22l2.5-1.5L20 22V2l-2.5 1.5L15 2l-3 1.5L9 2 6.5 3.5Z" />
    <path d="M8 10h8M8 14h5" />
  </svg>
);

/**
 * The "⋯" overflow menu on a payment row.
 *
 * Renders nothing when there is nothing to put in it, so a row never shows an
 * affordance that opens an empty menu.
 */
export function RowActionsMenu({
  actions,
  label,
}: {
  actions: RowAction[];
  label: string;
}) {
  if (actions.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={label}
          title={label}
          style={{
            ...actionBtn("#64748B"),
            padding: "6px 9px",
            lineHeight: 1,
          }}
        >
          <svg {...rowIconProps} width={15} height={15}>
            <circle cx="5" cy="12" r="1" />
            <circle cx="12" cy="12" r="1" />
            <circle cx="19" cy="12" r="1" />
          </svg>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {actions.map((a, i) => (
          <Fragment key={a.key}>
            {a.separatorBefore && i > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem
              disabled={a.disabled}
              onSelect={() => {
                // Let the menu close itself: Radix's MenuItem only calls
                // `onClose()` when the item-select event is NOT defaultPrevented
                // (@radix-ui/react-menu handleSelect), so calling
                // `preventDefault()` here would pin the dropdown open on top of
                // whatever panel the action just opened. The action is deferred
                // one tick instead, so the menu's focus restore finishes before
                // an inline panel (Notes / Received) takes focus.
                setTimeout(() => a.onSelect(), 0);
              }}
              className={
                a.destructive
                  ? "gap-2 text-[12.5px] text-destructive focus:text-destructive"
                  : "gap-2 text-[12.5px]"
              }
            >
              {a.icon}
              {a.label}
              {a.count != null && (
                <span className="ml-auto text-[11.5px] font-semibold text-muted-foreground">
                  {a.count}
                </span>
              )}
            </DropdownMenuItem>
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
